import { type ReactNode, useCallback } from "react";
import type { GestureResponderEvent } from "react-native";
import { AnimatePresence, type ColorTokens, type GetProps, Spinner, Stack, styled, Text, Theme, XStack } from "tamagui";
import { sizeTokens } from "../themes/token-size";
import { HapticsService, useMinimumLoadingTime } from "../utils";
import { Icon, type IconComponent } from "./Icon";
export type ButtonVariant = "primary" | "secondary" | "tertiary";
export type ButtonSize = "large" | "medium" | "small";
const buttonVariants = {
  variant: {
    primary: {
      backgroundColor: "$brandNavyDark",
      borderWidth: 0,
      hoverStyle: {
        backgroundColor: "$brandNavy"
      },
      pressStyle: {
        backgroundColor: "$brandNavy",
        scale: 0.97
      },
      focusVisibleStyle: {
        outlineWidth: 2,
        outlineColor: "$brandNavy",
        outlineStyle: "solid",
        outlineOffset: 2
      }
    },
    secondary: {
      backgroundColor: "$transparent",
      borderWidth: 1,
      borderColor: "$uiStroke",
      hoverStyle: {
        backgroundColor: "$transparent",
        borderColor: "$textPrimary"
      },
      pressStyle: {
        backgroundColor: "$transparent",
        borderColor: "$textPrimary",
        scale: 0.97
      },
      focusVisibleStyle: {
        outlineWidth: 2,
        outlineColor: "$brandNavy",
        outlineStyle: "solid",
        outlineOffset: 2
      }
    },
    tertiary: {
      backgroundColor: "$transparent",
      borderWidth: 0,
      hoverStyle: {
        backgroundColor: "$surfaceSecondary"
      },
      pressStyle: {
        backgroundColor: "$surfaceSecondary",
        scale: 0.97
      },
      focusVisibleStyle: {
        outlineWidth: 2,
        outlineColor: "$brandNavy",
        outlineStyle: "solid",
        outlineOffset: 2
      }
    }
  },
  size: {
    large: {
      height: "$si7",
      paddingHorizontal: "$sp4"
    },
    medium: {
      height: "$si5",
      paddingHorizontal: "$sp3"
    },
    small: {
      height: "$si4",
      paddingHorizontal: "$sp2"
    }
  },
  disabled: {
    true: {
      cursor: "not-allowed",
      opacity: 1,
      backgroundColor: "$surfaceSecondary",
      borderColor: "$transparent",
      hoverStyle: {
        backgroundColor: "$surfaceSecondary",
        borderColor: "$transparent"
      },
      pressStyle: {
        backgroundColor: "$surfaceSecondary",
        borderColor: "$transparent",
        scale: 1
      }
    }
  },
  fillWidth: {
    true: {
      width: "100%"
    },
    false: {
      width: "auto",
      minWidth: "auto"
    }
  }
} as const;
const StyledButton = styled(Stack, {
  name: "Button",
  tag: "button",
  role: "button",
  focusable: true,
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: "$brFull",
  // Use CSS transitions instead of Tamagui's animation system to avoid inline styles
  style: {
    transitionProperty: "background-color, border-color, transform",
    transitionDuration: "100ms",
    transitionTimingFunction: "ease-out"
  },
  variants: buttonVariants,
  defaultVariants: {
    variant: "primary",
    size: "medium",
    fillWidth: true
  }
});
const textVariants = {
  variant: {
    primary: {
      color: "$surfaceWhite"
    },
    secondary: {
      color: "$textPrimary"
    },
    tertiary: {
      color: "$brandNavyDark"
    }
  },
  size: {
    large: {
      fontSize: 16,
      lineHeight: 24
    },
    medium: {
      fontSize: 16,
      lineHeight: 24
    },
    small: {
      fontSize: 14,
      lineHeight: 16
    }
  },
  disabled: {
    true: {
      color: "$textDisabled"
    }
  }
} as const;
const ButtonText = styled(Text, {
  name: "ButtonText",
  tag: "span",
  fontFamily: "$body",
  fontWeight: "500",
  variants: textVariants,
  defaultVariants: {
    variant: "primary",
    size: "medium"
  }
});
const textColorTokens: Record<ButtonVariant, ColorTokens> = {
  primary: "$surfaceWhite",
  secondary: "$textPrimary",
  tertiary: "$brandNavyDark"
};
const buttonSizeToIconToken: Record<ButtonSize, `$icon${Capitalize<ButtonSize>}`> = {
  large: "$iconLarge",
  medium: "$iconMedium",
  small: "$iconSmall"
};
type StyledButtonProps = GetProps<typeof StyledButton>;
type BaseButtonProps = Omit<StyledButtonProps, "variant" | "size" | "onPressOut" | "children"> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /**
   * Skip the minimum loading display time (200ms) and transition instantly.
   * @default false
   */
  instantLoading?: boolean;
};
type IconOnlyButtonProps = BaseButtonProps & {
  icon: IconComponent;
  children?: never;
  iconLeft?: never;
  iconRight?: never;
};
type TextButtonProps = BaseButtonProps & {
  icon?: never;
  children: ReactNode;
  iconLeft?: IconComponent;
  iconRight?: IconComponent;
};
export type ButtonProps = IconOnlyButtonProps | TextButtonProps;

// Square sizes for icon-only buttons (width = height)
const iconOnlySizes: Record<ButtonSize, number> = {
  large: sizeTokens.si7,
  medium: sizeTokens.si5,
  small: sizeTokens.si4
};
export const Button = (props: ButtonProps) => {
  const {
    variant = "primary",
    size = "medium",
    loading,
    instantLoading = false,
    disabled,
    icon,
    iconLeft,
    iconRight,
    fillWidth = true,
    children,
    onPress,
    ...rest
  } = props;
  const isIconOnly = icon !== undefined;
  const debouncedLoading = useMinimumLoadingTime(loading);
  const showLoading = instantLoading ? loading ?? false : debouncedLoading;
  const handlePressOut = useCallback(() => {
    if (!disabled) {
      void HapticsService.selection();
    }
  }, [disabled]);
  const handlePress = useCallback((event: GestureResponderEvent) => {
    if (!disabled) {
      onPress?.(event);
    }
  }, [disabled, onPress]);
  const iconSize = buttonSizeToIconToken[size];
  const iconColor: ColorTokens = disabled ? "$textDisabled" : textColorTokens[variant];

  // For icon-only buttons, make width equal to height (square) and remove padding
  const iconOnlyStyles = isIconOnly ? {
    width: iconOnlySizes[size],
    paddingHorizontal: 0
  } : {};
  return <Theme name="v2" data-sentry-element="Theme" data-sentry-component="Button" data-sentry-source-file="Button.tsx">
      <StyledButton {...rest} variant={variant} size={size} disabled={disabled} fillWidth={isIconOnly ? false : fillWidth} onPressOut={handlePressOut} onPress={handlePress} {...iconOnlyStyles} data-sentry-element="StyledButton" data-sentry-source-file="Button.tsx">
        <XStack justifyContent="center" alignItems="center" gap="$sp1" position="relative" data-sentry-element="XStack" data-sentry-source-file="Button.tsx">
          {icon ? <Stack opacity={showLoading ? 0 : 1} animation="quick" animateOnly={["opacity"]} style={{
          outline: "none"
        }} pointerEvents="none">
              <Icon icon={icon} size={iconSize} color={iconColor} />
            </Stack> : null}
          {iconLeft ? <Stack opacity={showLoading ? 0 : 1} animation="quick" animateOnly={["opacity"]}>
              <Icon icon={iconLeft} size={iconSize} color={iconColor} />
            </Stack> : null}
          {children ? <ButtonText variant={variant} size={size} disabled={disabled} opacity={showLoading ? 0 : 1} animation="quick" animateOnly={["opacity"]}>
              {children}
            </ButtonText> : null}
          {iconRight ? <Stack opacity={showLoading ? 0 : 1} animation="quick" animateOnly={["opacity"]}>
              <Icon icon={iconRight} size={iconSize} color={iconColor} />
            </Stack> : null}
          <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="Button.tsx">
            {showLoading ? <Stack position="absolute" left={0} top={0} right={0} bottom={0} justifyContent="center" alignItems="center" enterStyle={{
            opacity: 0
          }} exitStyle={{
            opacity: 0
          }} animateOnly={["opacity"]} animation="quick" zIndex={100}>
                <Spinner flex={0} color={iconColor} key="loading-spinner" size="small" />
              </Stack> : null}
          </AnimatePresence>
        </XStack>
      </StyledButton>
    </Theme>;
};